import { useMediaQuery } from '../hooks';

// Badge icon mapping based on badge handle/name
const BADGE_ICONS = {
    '1st-workout-complete': '🎯',
    '10-workouts-badge': '💯',
    '50-workouts-badge': '⭐',
    '100-workouts-badge': '🌟',
    'perfect-month': '📅',
    'new-years-badge': '🎉',
    '4th-of-july-badge': '🎆',
    'halloween-badge': '🎃',
    'thanksgiving-badge': '🦃',
    'christmas-badge': '🎄',
    '6-month-loyalty-badge': '💎',
    'one-year-anniversary': '👑',
    '2-year-loyalty-badge': '🏰',
    'personal-record': '🏆',
    'default': '🏅'
};

// Helper function to get badge icon
const getBadgeIcon = (badgeHandle) => {
    return BADGE_ICONS[badgeHandle] || BADGE_ICONS['default'];
};

// Helper function to format badge name from handle
const formatBadgeName = (handle) => {
    return handle
        .replace(/-/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

function Badge({ badge, date, icon, title, locked = false, size = 'normal', transparent = false, noBorder = false }) {
    const isMobile = useMediaQuery('(max-width: 768px)');
    
    // Size multipliers - add 'small' size for mobile badges page
    let sizeMultiplier = 1;
    if (size === 'large') {
        sizeMultiplier = 2.5;
    } else if (size === 'small') {
        sizeMultiplier = 0.90; // 90% of normal size
    }
    
    // Make badge images larger on mobile (affects all sizes)
    const mobileImageScale = isMobile ? 1.4 : 1;
    const imageSize = 75 * sizeMultiplier * mobileImageScale;

    const badgeCellStyle = isMobile ? {
        width: `${165 * sizeMultiplier}px`,
        height: `${205 * sizeMultiplier}px`,
        padding: `${15.5 * sizeMultiplier}px ${7.5 * sizeMultiplier}px ${7.5 * sizeMultiplier}px ${7.5 * sizeMultiplier}px`,
        gap: `${7.5 * sizeMultiplier}px`,
        borderRadius: `${10.321 * sizeMultiplier}px`,
        border: `${0.258 * sizeMultiplier}px solid #000`,
        overflow: 'hidden',
        opacity: locked ? 0.4 : 1
    } : {
        width: `${165 * sizeMultiplier}px`,
        height: `${185 * sizeMultiplier}px`,
        padding: `${15.5 * sizeMultiplier}px ${7.5 * sizeMultiplier}px ${7.5 * sizeMultiplier}px ${7.5 * sizeMultiplier}px`,
        gap: `${7.5 * sizeMultiplier}px`,
        borderRadius: `${10.321 * sizeMultiplier}px`,
        border: `${0.258 * sizeMultiplier}px solid #000`,
        overflow: 'hidden',
        opacity: locked ? 0.4 : 1
    };

    const badgeTitleStyle = {
        flex: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        wordBreak: 'normal',
        hyphens: 'none',
        color: '#000',
        textAlign: 'center',
        fontFamily: 'Anton',
        fontSize: `${20 * sizeMultiplier}px`,
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '120%',
        textTransform: 'uppercase',
        whiteSpace: 'normal',
        overflowWrap: 'break-word'
    };

    const badgeDateStyle = {
        flexShrink: 0,
        color: '#000',
        textAlign: 'center',
        fontFamily: 'Poppins',
        fontSize: `${16.5 * sizeMultiplier}px`,
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '140%',
        letterSpacing: `${0.464 * sizeMultiplier}px`,
        textTransform: 'uppercase'
    };

    // Determine badge properties
    const badgeImage = badge?.fields?.Image || badge?.fields?.image || badge?.image;
    const badgeIcon = icon || (badge ? getBadgeIcon(badge.handle) : '🏅');
    const badgeTitle = title || (badge ? (badge.fields?.name || formatBadgeName(badge.handle)) : 'Badge');
    
    // Use earnedDate from badge if available, otherwise use provided date or today's date
    let badgeDate;
    if (date) {
        badgeDate = date;
    } else if (badge?.earnedDate) {
        const earnedDate = new Date(badge.earnedDate);
        badgeDate = earnedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
    } else {
        badgeDate = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
    }

    return (
        <div className={`tw-flex tw-flex-col tw-items-center tw-rounded-lg tw-relative ${transparent ? 'tw-bg-transparent' : 'tw-bg-gray-50'} ${!noBorder && !transparent ? 'tw-border tw-border-gray-200' : ''}`} style={badgeCellStyle}>
            <div className="tw-flex tw-items-center tw-justify-center" style={{width: `${imageSize}px`, height: `${imageSize}px`, aspectRatio: '1/1'}}>
                {badgeImage ? (
                    <img 
                        src={badgeImage} 
                        alt={badgeTitle}
                        className="tw-w-full tw-h-full tw-object-contain"
                        style={{ filter: locked ? 'grayscale(100%)' : 'none' }}
                    />
                ) : (
                    <span style={{ fontSize: `${imageSize}px`, lineHeight: 1, filter: locked ? 'grayscale(100%)' : 'none' }}>{badgeIcon}</span>
                )}
            </div>
            <h3 className="tw-text-black tw-m-0 tw-text-center" style={badgeTitleStyle}>
                {badgeTitle}
            </h3>
            <p className="tw-text-black tw-uppercase tw-text-center tw-m-0" style={badgeDateStyle}>
                {locked ? 'LOCKED' : badgeDate}
            </p>
        </div>
    );
}

export default Badge;
